import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Cable, AlertCircle, CheckCircle2, XCircle, Info } from 'lucide-react';
import {
  DEFAULT_INPUTS,
  NUMERIC_FIELD_SPECS,
  SYSTEM_TYPES,
  CONDUCTOR_MATERIALS,
  INSULATION_RATINGS,
  INSTALL_METHODS,
  REFERENCE_STANDARDS,
  GROUPING_OPTIONS,
  AWG_LABEL_BY_MM2,
  validateField,
} from '../../lib/cableSizeTools';
import { cableSizeToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));
const FT_PER_M = 3.28084;

function activeKeys(mode, systemType, inputMode, dutyCycle) {
  const keys = ['supplyVoltageV', 'cableLengthM', 'maxVoltageDropPercent'];
  keys.push(inputMode === 'power' ? 'loadPowerKw' : 'loadCurrentA');
  if (mode === 'advanced') {
    keys.push('ambientTempC', 'parallelConductors');
    if (systemType !== 'dc') keys.push('powerFactor');
    if (dutyCycle === 'intermittent') keys.push('dutyFactorPercent');
  }
  return keys;
}

const sizeLabel = (mm2, sizeDisplay) => {
  if (sizeDisplay === 'awg') {
    const awg = AWG_LABEL_BY_MM2[mm2];
    return awg ? `${awg} (${mm2} mm²)` : `${mm2} mm²`;
  }
  return `${mm2} mm²`;
};

const CableSizeCalculator = () => {
  const [mode, setMode] = useState('simple');
  const [systemType, setSystemType] = useState(DEFAULT_INPUTS.systemType);
  const [inputMode, setInputMode] = useState(DEFAULT_INPUTS.inputMode);
  const [conductorMaterial, setConductorMaterial] = useState(DEFAULT_INPUTS.conductorMaterial);
  const [lengthUnit, setLengthUnit] = useState('m');

  const [insulationRating, setInsulationRating] = useState(DEFAULT_INPUTS.insulationRating);
  const [installMethod, setInstallMethod] = useState(DEFAULT_INPUTS.installMethod);
  const [groupingCircuits, setGroupingCircuits] = useState(String(DEFAULT_INPUTS.groupingCircuits));
  const [dutyCycle, setDutyCycle] = useState(DEFAULT_INPUTS.dutyCycle);
  const [referenceStandard, setReferenceStandard] = useState(DEFAULT_INPUTS.referenceStandard);
  const [sizeDisplay, setSizeDisplay] = useState('mm2');

  const [raw, setRaw] = useState({
    loadCurrentA: String(DEFAULT_INPUTS.loadCurrentA),
    loadPowerKw: String(DEFAULT_INPUTS.loadPowerKw),
    supplyVoltageV: String(DEFAULT_INPUTS.supplyVoltageV),
    cableLengthM: String(DEFAULT_INPUTS.cableLengthM),
    maxVoltageDropPercent: String(DEFAULT_INPUTS.maxVoltageDropPercent),
    ambientTempC: String(DEFAULT_INPUTS.ambientTempC),
    powerFactor: String(DEFAULT_INPUTS.powerFactor),
    parallelConductors: String(DEFAULT_INPUTS.parallelConductors),
    dutyFactorPercent: String(DEFAULT_INPUTS.dutyFactorPercent),
  });
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const keys = useMemo(() => activeKeys(mode, systemType, inputMode, dutyCycle), [mode, systemType, inputMode, dutyCycle]);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const key of keys) {
      const err = validateField(SPEC_BY_KEY[key], raw[key]);
      if (err) errors[key] = err;
      else parsed[key] = Number(raw[key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [keys, raw]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const cableLengthM = lengthUnit === 'ft' ? values.cableLengthM / FT_PER_M : values.cableLengthM;
    const payload = {
      systemType,
      inputMode,
      conductorMaterial,
      supplyVoltageV: values.supplyVoltageV,
      cableLengthM,
      maxVoltageDropPercent: values.maxVoltageDropPercent,
      loadCurrentA: values.loadCurrentA,
      loadPowerKw: values.loadPowerKw,
    };
    if (mode === 'advanced') {
      Object.assign(payload, {
        ambientTempC: values.ambientTempC,
        insulationRating,
        installMethod,
        groupingCircuits: Number(groupingCircuits),
        powerFactor: values.powerFactor,
        parallelConductors: values.parallelConductors,
        dutyCycle,
        dutyFactorPercent: values.dutyFactorPercent,
        referenceStandard,
      });
    }

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      cableSizeToolApi.calculate(payload)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasErrors, values, mode, systemType, inputMode, conductorMaterial, lengthUnit,
    insulationRating, installMethod, groupingCircuits, dutyCycle, referenceStandard,
  ]);

  const numField = (key, { placeholder } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`cable-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} {spec.unit && <span className="normal-case text-muted-foreground/70">({spec.unit})</span>}
        </Label>
        <Input
          id={`cable-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `cable-${key}-error` : undefined}
          data-testid={`cable-${key}-input`}
        />
        {error && (
          <p id={`cable-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`cable-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const isAdvanced = mode === 'advanced';
  const showPowerFactor = isAdvanced && systemType !== 'dc';

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Cable className="h-5 w-5 text-autoxium-blue" />
                Cable Size Calculator
              </CardTitle>
              <CardDescription>
                Recommend the minimum cable cross-section for a DC, single-phase, or
                three-phase circuit. Results update live as you edit.
              </CardDescription>
            </div>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList data-testid="cable-mode-tabs">
                <TabsTrigger value="simple" data-testid="cable-mode-simple">Simple</TabsTrigger>
                <TabsTrigger value="advanced" data-testid="cable-mode-advanced">Advanced</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Circuit &amp; Load
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">System Type</Label>
                <Select value={systemType} onValueChange={setSystemType}>
                  <SelectTrigger data-testid="cable-system-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SYSTEM_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Specify Load As</Label>
                <Select value={inputMode} onValueChange={setInputMode}>
                  <SelectTrigger data-testid="cable-input-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Load Current (A)</SelectItem>
                    <SelectItem value="power">Load Power (kW)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {inputMode === 'power' ? numField('loadPowerKw') : numField('loadCurrentA')}
              {numField('supplyVoltageV')}
              <div className="space-y-2">
                <Label htmlFor="cable-cableLengthM" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Cable Length, One-Way <span className="normal-case text-muted-foreground/70">({lengthUnit})</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="cable-cableLengthM"
                    type="number"
                    step="any"
                    value={raw.cableLengthM}
                    onChange={(e) => setField('cableLengthM', e.target.value)}
                    className="font-mono"
                    aria-invalid={!!fieldErrors.cableLengthM}
                    data-testid="cable-cableLengthM-input"
                  />
                  <Select value={lengthUnit} onValueChange={setLengthUnit}>
                    <SelectTrigger className="w-20" data-testid="cable-length-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="m">m</SelectItem>
                      <SelectItem value="ft">ft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {fieldErrors.cableLengthM && (
                  <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.cableLengthM}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Route length from source to load — not the round-trip (go + return) distance.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Conductor Material</Label>
                <Select value={conductorMaterial} onValueChange={setConductorMaterial}>
                  <SelectTrigger data-testid="cable-material-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDUCTOR_MATERIALS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                {numField('maxVoltageDropPercent')}
                <p className="text-xs text-muted-foreground">
                  A larger voltage drop wastes power as heat and starves the load end of the
                  circuit — 3% is a common general limit; automotive DC runs are often held to 2–3%.
                </p>
              </div>
            </div>
          </div>

          {isAdvanced && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-md" data-testid="cable-advanced-fields">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Derating &amp; Installation
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 min-w-0">
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Insulation / Temperature Rating</Label>
                  <Select value={insulationRating} onValueChange={setInsulationRating}>
                    <SelectTrigger data-testid="cable-insulation-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INSULATION_RATINGS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Installation Method</Label>
                  <Select value={installMethod} onValueChange={setInstallMethod}>
                    <SelectTrigger data-testid="cable-install-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INSTALL_METHODS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {numField('ambientTempC')}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Circuits Grouped Together</Label>
                  <Select value={groupingCircuits} onValueChange={setGroupingCircuits}>
                    <SelectTrigger data-testid="cable-grouping-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GROUPING_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'circuit (none grouped)' : 'circuits bunched'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {showPowerFactor && numField('powerFactor')}
                {numField('parallelConductors')}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Duty Cycle</Label>
                  <Select value={dutyCycle} onValueChange={setDutyCycle}>
                    <SelectTrigger data-testid="cable-duty-cycle-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continuous">Continuous</SelectItem>
                      <SelectItem value="intermittent">Intermittent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {dutyCycle === 'intermittent' && numField('dutyFactorPercent')}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Standard / Reference Basis</Label>
                  <Select value={referenceStandard} onValueChange={setReferenceStandard}>
                    <SelectTrigger data-testid="cable-reference-standard-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REFERENCE_STANDARDS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Size Table Units</Label>
                  <Select value={sizeDisplay} onValueChange={setSizeDisplay}>
                    <SelectTrigger data-testid="cable-size-display-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm2">mm²</SelectItem>
                      <SelectItem value="awg">AWG / kcmil (nearest)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(referenceStandard === 'nec' || referenceStandard === 'iso6722') && (
                <p className="flex items-start gap-1.5 text-xs text-amber-700">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {referenceStandard === 'nec' ? 'NEC' : 'ISO 6722'} tables aren't implemented yet — this
                  result still uses the IEC 60364-5-52 (BS 7671) tables below.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="lg:col-span-2 space-y-4">
        {calcError ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="cable-calc-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
            </div>
          </CardContent></Card>
        ) : hasErrors ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground" data-testid="cable-results-placeholder">
              Fix the highlighted fields to see a recommendation.
            </p>
          </CardContent></Card>
        ) : !results ? (
          <Card className="technical-card"><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground" data-testid="cable-results-loading">Calculating…</p>
          </CardContent></Card>
        ) : mode === 'simple' ? (
          <SimpleResults results={results} sizeDisplay="mm2" />
        ) : (
          <AdvancedResults results={results} sizeDisplay={sizeDisplay} />
        )}
      </div>
    </div>
  );
};

const SimpleResults = ({ results }) => (
  <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="cable-simple-results">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">Recommended Cable Size</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <p className="font-mono text-4xl font-black text-autoxium-blue" data-testid="cable-recommended-size">
        {results.recommendedSizeMm2} mm²
      </p>
      {(results.exceedsStandardRangeVoltageDrop || results.exceedsStandardRangeAmpacity) && (
        <div className="flex items-start gap-2 text-xs text-destructive font-bold" data-testid="cable-exceeds-range-warning">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Even the largest standard size (240 mm²) doesn't fully satisfy this circuit —
          consider parallel conductors or a specialist review.
        </div>
      )}
      <div className="pt-3 border-t border-structure space-y-1.5 text-sm font-mono">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Load Current</span>
          <span className="font-bold">{results.loadCurrentA} A</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Voltage Drop</span>
          <span className="font-bold">{results.actualVoltageDropPercent}% ({results.actualVoltageDropV} V)</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground pt-2 border-t border-structure">
        This size also clears a default current-carrying-capacity check (30°C ambient,
        clipped-direct, PVC 70°C, no grouping) so it's safe as a first-pass pick — switch
        to Advanced to control every derating factor and see the full breakdown.
      </p>
    </CardContent>
  </Card>
);

const AdvancedResults = ({ results, sizeDisplay }) => {
  const rec = sizeLabel(results.recommendedSizeMm2, sizeDisplay);
  return (
    <div className="space-y-4">
      <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="cable-advanced-results">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Recommended Cable Size</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="font-mono text-4xl font-black text-autoxium-blue" data-testid="cable-recommended-size">{rec}</p>
          <Badge variant="outline" className="font-mono text-[10px]" data-testid="cable-governing-badge">
            {results.governingConstraint === 'voltage_drop' ? 'Voltage drop governs' : 'Current capacity governs'}
          </Badge>
          {(results.exceedsStandardRangeVoltageDrop || results.exceedsStandardRangeAmpacity) && (
            <div className="flex items-start gap-2 text-xs text-destructive font-bold" data-testid="cable-exceeds-range-warning">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Even 240 mm² doesn't fully satisfy this circuit — consider parallel
              conductors or a specialist review.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-structure">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Voltage-Drop-Limited</p>
              <p className="font-mono text-sm font-bold">{sizeLabel(results.voltageDropLimitedSizeMm2, sizeDisplay)}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Ampacity-Limited</p>
              <p className="font-mono text-sm font-bold">{sizeLabel(results.ampacityLimitedSizeMm2, sizeDisplay)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Load Current</span><span className="font-bold">{results.loadCurrentA} A</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Utilization</span><span className="font-bold">{results.actualUtilizationPercent}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VDrop</span><span className="font-bold">{results.actualVoltageDropPercent}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Power Loss</span><span className="font-bold">{results.actualPowerLossW} W</span></div>
          </div>
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="cable-nearby-table">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Nearby Standard Sizes</CardTitle>
          <CardDescription>Margin check either side of the recommended size.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">Ampacity (A)</TableHead>
                <TableHead className="text-right">Util. %</TableHead>
                <TableHead className="text-right">VDrop %</TableHead>
                <TableHead className="text-right">Loss (W)</TableHead>
                <TableHead className="text-center">OK</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.table.map((row) => {
                const ok = row.meetsVoltageDrop && row.meetsAmpacity;
                const isRecommended = row.sizeMm2 === results.recommendedSizeMm2;
                return (
                  <TableRow key={row.sizeMm2} className={isRecommended ? 'bg-autoxium-blue/5' : ''} data-testid={`cable-row-${row.sizeMm2}`}>
                    <TableCell className="font-mono text-xs font-bold">{sizeLabel(row.sizeMm2, sizeDisplay)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.ampacityA}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.utilizationPercent}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${row.meetsVoltageDrop ? '' : 'text-destructive'}`}>{row.voltageDropPercent}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.powerLossW}</TableCell>
                    <TableCell className="text-center">
                      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <XCircle className="h-4 w-4 text-destructive inline" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="cable-assumptions">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Assumptions &amp; Basis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
          <div>
            <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">basis</Badge>
            {results.referenceBasisLabel}. Ampacity and voltage-drop-per-metre figures are
            taken from BS 7671:2018 Appendix 4 Tables 4D1A/4D1B (PVC, 70°C) and
            4E1A/4E1B (XLPE/thermosetting, 90°C) — the UK implementation of
            IEC 60364-5-52 — at the conductor's rated operating temperature.
          </div>
          <div>
            <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">factors used</Badge>
            Ambient correction ×{results.ambientFactor} (IEC 60364-5-52 Table B.52.14) ·
            Grouping correction ×{results.groupingFactor} (Table B.52.17).
          </div>
          <div>
            <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">approximation</Badge>
            Aluminum figures are derived, not directly tabulated: ampacity ≈ 0.78× the
            equivalent copper size, and voltage-drop resistance ≈ 1.64× copper's (the
            IEC 60228 resistivity ratio) — verify against a manufacturer datasheet for a
            final design.
          </div>
          <div>
            <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">behavior</Badge>
            Duty Cycle and Power Factor are engineering-judgment inputs, not values pulled
            from a specific standard table — Intermittent Duty Factor scales the effective
            load current used in the ampacity check; Power Factor blends the tabulated
            resistive (r) and reactive (x) components for AC voltage drop.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CableSizeCalculator;
